import { useEffect, useMemo, useRef, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Box } from '@mui/material';
import { GridActionsCellItem, GridRowModes, GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import {
  AddButtonUi,
  AutoCompleteSelectServiceUI,
  DataGridIdentifiers,
  DatagridPremiumUi,
  FormErrorUI,
  InputUI,
} from '../Interface';
import { dataGridPremiumColumns, normalizeNumberInput, parseLocaleNumber } from '../../utils/helper';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { InvManagementMethod } from '../../config/itemGroup';
import masterDataService from '../../service/masterData.service';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../utils/itemAutocomplete';

const EditToolbar = ({ disabled, push, defaultItem }) => {
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    await push({
      ...defaultItem,
      id: uuidv4(),
      isNew: true,
    });

    await validateField(`assemblyDefinitionLine.${values.assemblyDefinitionLine.length ?? 0}.itemCode`);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const normalizeSingleItemResponse = (response) => {
  if (Array.isArray(response?.body)) {
    return response.body[0] ?? null;
  }

  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  return response?.body ?? response ?? null;
};

export const AssemblyDefinitionLine = ({ disabled, defaultItem, autocompleteData }) => {
  const focusedFieldRef = useRef(null);
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [itemDetailsByCode, setItemDetailsByCode] = useState({});
  const [editorResetVersionByRowId, setEditorResetVersionByRowId] = useState({});

  const getValueIndex = (formValues, id) => {
    return formValues.assemblyDefinitionLine.findIndex((row) => row.id === id);
  };

  const rememberItemDetails = (item) => {
    if (!item?.itemCode) {
      return;
    }

    setItemDetailsByCode((prev) => {
      if (prev?.[item.itemCode]) {
        return prev;
      }

      return {
        ...prev,
        [item.itemCode]: item,
      };
    });
  };

  const getItemMetaByCode = async (itemCode) => {
    if (!itemCode) {
      return null;
    }

    const itemFromRows = (values?.assemblyDefinitionLine ?? []).find((row) => row?.itemCode === itemCode)?.item;
    if (itemFromRows?.itemCode === itemCode) {
      return itemFromRows;
    }

    const cachedItem = itemDetailsByCode?.[itemCode];
    if (cachedItem) {
      return cachedItem;
    }

    try {
      const response = await masterDataService.itemsFromTableToFormByItemCode(itemCode);
      const item = normalizeSingleItemResponse(response);
      if (item) {
        rememberItemDetails(item);
      }
      return item;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const rowItems = (values?.assemblyDefinitionLine ?? [])
      .map((row) => row?.item)
      .filter((item) => item?.itemCode);

    if (rowItems.length > 0) {
      setItemDetailsByCode((prev) => {
        const next = { ...prev };
        let changed = false;

        rowItems.forEach((item) => {
          if (!next[item.itemCode]) {
            next[item.itemCode] = item;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    }
  }, [values?.assemblyDefinitionLine]);

  useEffect(() => {
    const itemCodesToResolve = (values?.assemblyDefinitionLine ?? [])
      .map((row) => row?.itemCode)
      .filter((itemCode) => Boolean(itemCode) && !itemDetailsByCode?.[itemCode]);

    if (!itemCodesToResolve.length) {
      return;
    }

    let cancelled = false;

    Promise.all(itemCodesToResolve.map((itemCode) => getItemMetaByCode(itemCode)))
      .then((items) => {
        if (cancelled) {
          return;
        }

        items.filter(Boolean).forEach((item) => {
          rememberItemDetails(item);
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [itemDetailsByCode, values?.assemblyDefinitionLine]);

  const handleItemChange = async (valueIndex, value) => {
    const selectedItem = await getItemMetaByCode(value);
    const rowId = values?.assemblyDefinitionLine?.[valueIndex]?.id;

    if (selectedItem?.invManagementMethod === InvManagementMethod.BATCH) {
      setFieldValue(`assemblyDefinitionLine.${valueIndex}.itemCode`, null);
      setFieldValue(`assemblyDefinitionLine.${valueIndex}.item`, null);
      setFieldValue(`assemblyDefinitionLine.${valueIndex}.quantityUnitId`, null);
      if (rowId != null) {
        setEditorResetVersionByRowId((prev) => ({
          ...prev,
          [rowId]: (prev?.[rowId] ?? 0) + 1,
        }));
        apiRef.current.setEditCellValue({
          id: rowId,
          field: 'itemCode',
          value: null,
        });
      }
      addNotification({
        content: 'Sarzsos cikk nem vehető fel az összeállítás tételei közé!',
        type: NotificationType.ERROR,
      });
      await validateField(`assemblyDefinitionLine.${valueIndex}.itemCode`);
      return false;
    }

    if (value && values.itemCode && value === values.itemCode) {
      setFieldValue(`assemblyDefinitionLine.${valueIndex}.itemCode`, null);
      setFieldValue(`assemblyDefinitionLine.${valueIndex}.item`, null);
      setFieldValue(`assemblyDefinitionLine.${valueIndex}.quantityUnitId`, null);
      if (rowId != null) {
        setEditorResetVersionByRowId((prev) => ({
          ...prev,
          [rowId]: (prev?.[rowId] ?? 0) + 1,
        }));
        apiRef.current.setEditCellValue({
          id: rowId,
          field: 'itemCode',
          value: null,
        });
      }
      addNotification({
        content: 'A fejben szereplő cikk nem vehető fel a tételek közé!',
        type: NotificationType.ERROR,
      });
      await validateField(`assemblyDefinitionLine.${valueIndex}.itemCode`);
      return false;
    }

    setFieldValue(`assemblyDefinitionLine.${valueIndex}.itemCode`, value);
    setFieldValue(`assemblyDefinitionLine.${valueIndex}.item`, selectedItem ?? null);
    setFieldValue(
      `assemblyDefinitionLine.${valueIndex}.quantityUnitId`,
      selectedItem?.quantityUnitId ?? selectedItem?.quantityUnit?.id ?? null
    );
    return true;
  };

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'itemCode',
          headerName: 'Alapanyag',
          type: 'string',
          editable: true,
          sortable: false,
          flex: 1.4,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const rowItemCode = values.assemblyDefinitionLine?.[valueIndex]?.itemCode;
            const item = values.assemblyDefinitionLine?.[valueIndex]?.item ?? itemDetailsByCode?.[rowItemCode];
            return [item?.itemCode ?? rowItemCode, item?.itemName].filter(Boolean).join(' - ');
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const rowItemCode = values.assemblyDefinitionLine?.[valueIndex]?.itemCode;
            const item = values.assemblyDefinitionLine?.[valueIndex]?.item ?? itemDetailsByCode?.[rowItemCode];

            return (
              <Field
                key={`assembly-line-item-${data.id}-${editorResetVersionByRowId?.[data.id] ?? 0}`}
                name={`assemblyDefinitionLine.${valueIndex}.itemCode`}
                type="text"
                variant="standard"
                as={AutoCompleteSelectServiceUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.assemblyDefinitionLine?.[valueIndex]?.itemCode} />}
                selectedValue={rowItemCode}
                selectedLabelValue={[item?.itemCode ?? rowItemCode, item?.itemName].filter(Boolean).join(' - ')}
                service={itemAutocompleteService}
                serviceRequestBuilder={itemAutocompleteRequestBuilder}
                searchModeEnabled
                onChange={async (_e, newVal) => {
                  const val = newVal?.value ?? null;

                  const changed = await handleItemChange(valueIndex, val);
                  if (!changed) return;

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: val,
                  });
                }}
                onBlur={async () => await validateField(`assemblyDefinitionLine.${valueIndex}.itemCode`)}
              />
            );
          },
        },
        {
          field: 'qty',
          headerName: 'Mennyiség',
          type: 'string',
          editable: true,
          sortable: false,
          flex: 0.8,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.assemblyDefinitionLine?.[valueIndex]?.qty;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`assemblyDefinitionLine.${valueIndex}.qty`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'qty'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.assemblyDefinitionLine?.[valueIndex]?.qty} />}
                onChange={(e) => {
                  const val = normalizeNumberInput(e.target?.value);

                  setFieldValue(`assemblyDefinitionLine.${valueIndex}.qty`, val);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`assemblyDefinitionLine.${valueIndex}.qty`)}
              />
            );
          },
        },
        {
          field: 'quantityUnit',
          headerName: 'Me.',
          type: 'string',
          sortable: false,
          flex: 0.5,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            const quantityUnitId =
              values?.assemblyDefinitionLine?.[valueIndex]?.quantityUnitId ??
              values?.assemblyDefinitionLine?.[valueIndex]?.item?.quantityUnit?.id ??
              values?.assemblyDefinitionLine?.[valueIndex]?.item?.quantityUnitId;
            const quantityUnit = autocompleteData?.quantityUnit?.find((unit) => unit.id === quantityUnitId);
            return quantityUnit?.code ?? values?.assemblyDefinitionLine?.[valueIndex]?.item?.quantityUnit?.code ?? '';
          },
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const rowItemCode = values.assemblyDefinitionLine?.[valueIndex]?.itemCode;
            const item = values.assemblyDefinitionLine?.[valueIndex]?.item ?? itemDetailsByCode?.[rowItemCode];
            const quantityUnitId =
              values?.assemblyDefinitionLine?.[valueIndex]?.quantityUnitId ??
              item?.quantityUnit?.id ??
              item?.quantityUnitId;
            const quantityUnit = autocompleteData?.quantityUnit?.find((unit) => unit.id === quantityUnitId);
            return quantityUnit?.code ?? item?.quantityUnit?.code ?? '';
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Szerk.',
          sortable: false,
          getActions: ({ id }) => [
            <GridActionsCellItem
              key={`delete-${id}`}
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ],
        },
      ]),
    [autocompleteData, disabled, editorResetVersionByRowId, errors, itemDetailsByCode, setFieldValue, validateField, values]
  );

  return (
    <div className="mx-4 mb-5">
      <div className="mb-2 text-sm font-medium">Tételek</div>
      <FieldArray name="assemblyDefinitionLine">
        {({ remove, push }) => (
          <Box className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <DatagridPremiumUi
              apiRef={apiRef}
              datagridIdentifier={DataGridIdentifiers.assemblyDefinitionLine}
              height={420}
              columns={columns(remove)}
              rows={values?.assemblyDefinitionLine ?? []}
              editMode="row"
              slots={{
                toolbar: EditToolbar,
              }}
              slotProps={{
                toolbar: {
                  disabled,
                  push,
                  defaultItem,
                },
              }}
              onCellDoubleClick={(_params, event) => {
                event.defaultMuiPrevented = true;
              }}
              headerFilters={false}
              getRowHeight={() => 'auto'}
              onCellClick={(params, event) => {
                if (disabled) return;

                focusedFieldRef.current = params.field;
                const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                for (const rowId of rowsInEditMode) {
                  if (params.row.id.toString() !== rowId) {
                    apiRef.current.stopRowEditMode({ id: rowId });
                  }
                }

                const rowMode = apiRef.current.getRowMode(params.row.id);
                if (rowMode === GridRowModes.View) {
                  apiRef.current.startRowEditMode({ id: params.row.id });
                }
              }}
              onRowEditStop={() => {
                focusedFieldRef.current = null;
              }}
            />
          </Box>
        )}
      </FieldArray>
    </div>
  );
};

export const mapAssemblyDefinitionLinesForSubmit = (lines = []) =>
  lines.map((line) => {
    const parsedQty = parseLocaleNumber(line.qty);

    return {
      ...(typeof line.id === 'number' ? { id: line.id } : {}),
      itemCode: line.itemCode,
      qty: Number.isFinite(parsedQty) ? parsedQty : 0,
    };
  });
